import "./TriviaQuestion.scss";

import { Alert, Button, Flex, Text } from "@fluentui/react-northstar";
import { NominalQuestion } from "@teams/types";
import { SurveyResponsesDTO } from "@teams/types/api";
import React, { useEffect, useMemo, useState } from "react";

import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";

export const TriviaQuestionResults: React.FC<{
  surveyId: string;
  question: NominalQuestion;
  index: number;
}> = ({ surveyId, question, index }) => {
  const { teamsContext } = useTeamsContext();
  const [results, setResults] = useState<SurveyResponsesDTO>();

  useEffect(() => {
    (async () => {
      const r = await surveyApi.surveyResponses(teamsContext, surveyId);
      if (!isError(r)) {
        setResults(r);
      }
    })();
  }, [surveyId, question.id, teamsContext]);

  const correctResponses = useMemo(() => {
    if (!results) {
      return [];
    }

    const correctChoice = question.choices.find(c => c.correct)!;
    return results.responses.filter(r =>
      r.responses.some(qr => qr.value.includes(correctChoice.id))
    );
  }, [question, results]);

  const userChoice = useMemo(() => {
    if (!results?.userResponse) {
      return undefined;
    }

    return results.userResponse.responses.find(ur => ur.questionId === question.id);
  }, [question.id, results?.userResponse]);

  return (
    <Flex column gap="gap.medium">
      <Text content={`${index}) ${question.label}`} weight="bold" size="large" />

      <Flex column>
        {question.choices.map(choice => {
          const isChoice = userChoice?.value.includes(choice.id);

          return (
            <div key={choice.id} style={{ marginBottom: ".2rem" }}>
              <Button
                content={choice.value}
                className={isChoice ? "trivia-question-results__button-selected" : ""}
                icon={
                  <PollyIcon
                    icon={choice.correct ? "correct-icon" : "incorrect-icon"}
                    className={
                      choice.correct
                        ? "trivia-question-results__button-correct"
                        : "trivia-question-results__button-wrong"
                    }
                    inheritColor
                  />
                }
                fluid
              />
            </div>
          );
        })}
      </Flex>

      {!userChoice && <Alert danger content={<Text align="center">You did not vote</Text>} />}

      {correctResponses.length === 0 && (
        <Text weight="bold" content="😞 Nobody got it correct" align="center" />
      )}

      {correctResponses.length > 0 && (
        <Text
          weight="bold"
          align="center"
          content={`${correctResponses.length} correct guesses by: ${correctResponses
            .map(r => r.user.name)
            .join(", ")}`}
        />
      )}
    </Flex>
  );
};
